import React, { Fragment, useEffect, useId, useState } from 'react';
import { InfoHint } from '@/components/ui/info-hint';
import {
  HELP_CENTER_URL,
  SUPPORT_BOOKING_NUMBER,
  SUPPORT_BOOKING_NUMBER_HINT,
  SUPPORT_HOURS,
  SUPPORT_IATA,
  SUPPORT_PHONE,
  SUPPORT_TIME_ZONE_LABEL,
  formatSupportHoursRule,
  isSupportOpen,
} from '@/lib/supportHours';

/** How often the open/closed status is re-read while the menu stays open. */
const STATUS_REFRESH_MS = 60_000;

const linkClass =
  'text-orange-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:text-orange-400';

/**
 * AR-408 — the support block of the user menu: a "Support" heading, the
 * support line, a live open/closed status against Eastern Time with the full
 * hours one tap away, then the IATA and booking numbers. Mounted only while
 * the menu is open, so the status is computed fresh on every open and the
 * hours fold back up when the menu closes.
 */
export const SupportContact: React.FC = () => {
  const [open, setOpen] = useState(() => isSupportOpen());
  const [showHours, setShowHours] = useState(false);
  const hoursId = useId();

  useEffect(() => {
    setOpen(isSupportOpen());
    const timer = window.setInterval(() => setOpen(isSupportOpen()), STATUS_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="border-b border-gray-200 px-4 py-3 text-xs dark:border-[rgba(255,117,64,0.15)]">
      <div className="mb-1 text-gray-500 dark:text-[#9CA8A9]">Support</div>
      <div className="font-medium text-gray-900 dark:text-[#EAEAEA]">{SUPPORT_PHONE}</div>
      <div
        role="status"
        className="mt-0.5 flex items-center gap-1.5 text-gray-500 dark:text-[#9CA8A9]"
      >
        <span
          aria-hidden
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${open ? 'bg-green-500' : 'bg-gray-400'}`}
        />
        <span>{open ? 'Support is open' : 'Support is closed'}</span>
      </div>
      <button
        type="button"
        className={`mt-0.5 ${linkClass}`}
        aria-expanded={showHours}
        aria-controls={hoursId}
        onClick={() => setShowHours((prev) => !prev)}
      >
        {showHours ? 'Hide hours' : 'See hours'}
      </button>
      {showHours && (
        <div id={hoursId} className="mt-1.5 text-gray-500 dark:text-[#9CA8A9]">
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
            {SUPPORT_HOURS.map((rule) => (
              <Fragment key={rule.label}>
                <dt>{rule.label}</dt>
                <dd className="text-gray-700 dark:text-[#EAEAEA]">{formatSupportHoursRule(rule)}</dd>
              </Fragment>
            ))}
          </dl>
          <div className="mt-1">
            All times {SUPPORT_TIME_ZONE_LABEL}.{' '}
            <a href={HELP_CENTER_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>
              Help page
            </a>
          </div>
        </div>
      )}
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-gray-500 dark:text-[#9CA8A9]">
        <span>
          IATA <b className="text-gray-700 dark:text-[#EAEAEA]">{SUPPORT_IATA}</b>
        </span>
        <span className="inline-flex items-center gap-1">
          Booking <b className="text-gray-700 dark:text-[#EAEAEA]">{SUPPORT_BOOKING_NUMBER}</b>
          <InfoHint label="About the booking number" side="top">
            {SUPPORT_BOOKING_NUMBER_HINT}
          </InfoHint>
        </span>
      </div>
    </div>
  );
};
